import React, { useMemo } from'react';
import { useNavigate } from'react-router-dom';
import { Package, CheckCircle2, Activity, AlertTriangle } from'lucide-react';
import { useLayout } from'../components/Layout';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import DataTable from'../components/tables/DataTable';
import KPICard from'../components/charts/KPICard';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader from'../components/PageLoader';
import { formatTimeRangeLabel } from'../components/TimeRangePicker';
import clsx from'clsx';

export default function Products() {
 const navigate = useNavigate();
 const { timeRange } = useLayout();

 const { data: productsResp, isPending } = useApiQuery(
 ['products', timeRange],
 () => api.getProducts(timeRange),
 { refetchInterval: 60000 }
 );

 // The backend now returns `{ products, summary }`. Stay backward-compatible
 // with the old array shape just in case a cached/older response arrives.
 const products = useMemo(
 () => (Array.isArray(productsResp) ? productsResp : (productsResp?.products || [])),
 [productsResp]
 );

 // Executive summary across all products.
 //
 // Prefer the backend's AUTHORITATIVE, deduplicated summary — it counts each
 // unique API once (an API shared by multiple products would otherwise be
 // double-counted) and uses the same served-traffic + IsRequestSuccess
 // definition as the Executive Dashboard, so the headline reconciles with it.
 // Fall back to summing per-product rows only for the legacy array shape.
 const rollup = useMemo(() => {
 const s = (!Array.isArray(productsResp) && productsResp?.summary) || null;
 if (s) {
 return {
 productCount: s.productCount ?? products.length,
 published: s.published ?? products.filter((p) => p.state === 'published').length,
 totalCalls: s.totalCalls ?? 0,
 errorRate: s.errorRate != null ? s.errorRate : null,
 };
 }
 const totalCalls = products.reduce((acc, p) => acc + (Number(p.totalCalls) || 0), 0);
 const totalErrors = products.reduce((acc, p) => acc + (Number(p.totalErrors) || 0), 0);
 return {
 productCount: products.length,
 published: products.filter((p) => p.state === 'published').length,
 totalCalls,
 errorRate: totalCalls > 0 ? (totalErrors / totalCalls) * 100 : null,
 };
 }, [productsResp, products]);

 const columns = [  {
 headerName:'Product',
 field:'displayName',
 flex: 2,
 cellRenderer: (params) => (
 <span className={clsx(
 params.data?._synthetic
 ?'font-medium text-gray-500 italic'
 :'font-medium text-brand-600 cursor-pointer hover:underline'
 )}>
 {params.value}
 </span>
 ),
 },  {
 headerName:'State',
 field:'state',
 width: 120,
 cellRenderer: (params) => {
 if (params.data?._synthetic) {
 return <span className="text-xs text-gray-400">—</span>;
 }
 return (
 <span className={clsx(
'px-2 py-0.5 rounded-full text-xs font-medium',
 params.value ==='published'
 ?'bg-emerald-100 text-emerald-700'
 :'bg-gray-100 text-gray-600'
 )}>
 {params.value ==='published' ?'Published' :'Not published'}
 </span>
 );
 },
 },
 { headerName:'APIs', field:'apiCount', width: 80, type:'numericColumn',
 valueFormatter: (p) => (p.data?._synthetic || p.value == null) ?'—' : p.value },
 { headerName:'Subscriptions', field:'subscriptionCount', width: 120, type:'numericColumn',
 valueFormatter: (p) => (p.data?._synthetic || p.value == null) ?'—' : p.value },
 {
 headerName:'Calls',
 field:'totalCalls',
 width: 120,
 type:'numericColumn',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 },
 {
 headerName:'Error Rate',
 field:'errorRate',
 width: 120,
 type:'numericColumn',
 valueFormatter: (p) =>`${(p.value || 0).toFixed(1)}%`,
 cellStyle: (p) => p.value > 5 ? { color:'#ef4444' } : {},
 },
 {
 headerName:'Avg Latency',
 field:'avgLatency',
 width: 120,
 type:'numericColumn',
 valueFormatter: (p) => (p.data?._synthetic || p.value == null) ?'—' :`${p.value}ms`,
 },
 ];

 return (
 <div className="space-y-6">
 <Breadcrumbs />
 <div>
 <h1 className="text-xl font-bold">Products</h1>
 <p className="text-sm text-gray-500 mt-0.5">
 All APIM products and their usage.
 <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
 last {formatTimeRangeLabel(timeRange)}
 </span>
 </p>
 </div>

 {/* Executive summary */}
 {!isPending && (
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
 <ErrorBoundary name="Products count KPI">
 <KPICard
 title="Products"
 value={rollup.productCount}
 format="number"
 icon={Package}
 color="brand"
 help="Total number of APIM products defined in this service."
 />
 </ErrorBoundary>
 <ErrorBoundary name="Published KPI">
 <KPICard
 title="Published"
 value={rollup.published}
 format="number"
 icon={CheckCircle2}
 color="emerald"
 help="Products in the 'published' state — visible to and subscribable by developers."
 subtitle={`${rollup.productCount - rollup.published} not published`}
 />
 </ErrorBoundary>     <ErrorBoundary name="Total Calls KPI">
 <KPICard
 title="Total Calls"
 value={rollup.totalCalls}
 format="number"
 icon={Activity}
 color="violet"
 help="Combined API calls across all products in the selected range."
 />
 </ErrorBoundary>
 <ErrorBoundary name="Error Rate KPI">
 <KPICard
 title="Error Rate"
 value={rollup.errorRate}
 format="percent"
 icon={AlertTriangle}
 color={rollup.errorRate != null && rollup.errorRate > 5 ?'red' :'emerald'}
 help="Share of all product calls that returned an error (HTTP 4xx/5xx) in the selected range."
 />
 </ErrorBoundary>
 </div>
 )}

 <ErrorBoundary>
 {isPending ? (
 <PageLoader message="Loading products…" />
 ) : (  <DataTable
 data={products || []}
 columns={columns}
 title={`${(products || []).filter((p) => !p._synthetic).length} Products`}
 subtitle="Click a product to see its usage, cost and subscriptions."
 height={600}
 exportFilename="products-overview"  onRowClick={(row) => {
 if (row?._synthetic) return; // "Other / Unassigned" isn't a real product
 navigate(`/products/${row.id}`);
 }}
 />
 )}
 </ErrorBoundary>
 </div>
 );
}
